<template>
    <ModalOverlay v-if="show" @click.stop>
        <ModalContainer @click.stop>
            <ModalHeader>
                <ModalTitle>
                    API 연결 관리
                </ModalTitle>
                <CloseButton @click="closeModal">✕</CloseButton>
            </ModalHeader>

            <ModalContent>
                <!-- * API 연결 -->
                <FormSection>
                    <FormGrid>
                        <FormGroup>
                            <FormLabel>API 서비스 선택</FormLabel>
                            <FormSelect v-model="formData.apiType" @change="onApiTypeChange">
                                <option value="">API 서비스를 선택하세요</option>
                                <option value="popbill">팝빌 (전자세금계산서, 현금영수증, 문자, 카카오톡)</option>
                                <option value="barobill">바로빌 (통장내역조회, 계좌조회)</option>
                            </FormSelect>
                            <ErrorMessage v-if="errors.apiType">{{ errors.apiType }}</ErrorMessage>
                        </FormGroup>
                    </FormGrid>
                </FormSection>

                <!-- * 팝빌 등록 섹션 -->
                <FormSection v-if="formData.apiType === 'popbill'">
                    <FormGrid>
                        <FormGroup>
                            <FormLabel>사업자번호</FormLabel>
                            <FormInput v-model="formData.popbillCorpNum" type="text" placeholder="사업자등록번호를 입력하세요"
                                :class="{ 'error': errors.popbillCorpNum }" />
                            <ErrorMessage v-if="errors.popbillCorpNum">{{ errors.popbillCorpNum }}</ErrorMessage>
                        </FormGroup>
                        <FormGroup>
                            <FormLabel>API Key</FormLabel>
                            <FormInput v-model="formData.popbillApiKey" type="text"
                                placeholder="팝빌에서 발급받은 API Key를 입력하세요" :class="{ 'error': errors.popbillApiKey }" />
                            <ErrorMessage v-if="errors.popbillApiKey">{{ errors.popbillApiKey }}</ErrorMessage>
                        </FormGroup>
                    </FormGrid>
                </FormSection>

                <!-- * 바로빌 등록 섹션 -->
                <FormSection v-if="formData.apiType === 'barobill'">
                    <SectionTitle v-if="isAddAccountMode"
                        style="color: #0f4c5c; font-weight: 600; margin-bottom: 15px;">
                        🔐 계정 추가 - {{ formData.barobillCorpName }}
                    </SectionTitle>

                    <FormGrid3>
                        <FormGroup>
                            <FormLabel>사업자번호</FormLabel>
                            <FormInput v-model="formData.barobillCorpNum" type="text" placeholder="사업자번호를 입력하세요 (- 빼고)"
                                :class="{ 'error': errors.barobillCorpNum }" :readonly="isAddAccountMode" />
                            <ErrorMessage v-if="errors.barobillCorpNum">{{ errors.barobillCorpNum }}</ErrorMessage>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>회사명</FormLabel>
                            <FormInput v-model="formData.barobillCorpName" type="text" placeholder="회사명을 입력하세요"
                                :class="{ 'error': errors.barobillCorpName }" :readonly="isAddAccountMode" />
                            <ErrorMessage v-if="errors.barobillCorpName">{{ errors.barobillCorpName }}</ErrorMessage>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>대표자명</FormLabel>
                            <FormInput v-model="formData.barobillCEOName" type="text" placeholder="대표자명을 입력하세요"
                                :class="{ 'error': errors.barobillCEOName }" :readonly="isAddAccountMode" />
                            <ErrorMessage v-if="errors.barobillCEOName">{{ errors.barobillCEOName }}</ErrorMessage>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>업태</FormLabel>
                            <FormInput v-model="formData.barobillBizType" type="text" placeholder="업태를 입력하세요"
                                :readonly="isAddAccountMode" />
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>업종</FormLabel>
                            <FormInput v-model="formData.barobillBizClass" type="text" placeholder="업종을 입력하세요"
                                :readonly="isAddAccountMode" />
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>이메일</FormLabel>
                            <FormInput v-model="formData.barobillEmail" type="email" placeholder="이메일을 입력하세요" />
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>전화번호</FormLabel>
                            <FormInput v-model="formData.barobillTEL" type="text" placeholder="전화번호를 입력하세요" />
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>계정정보 - 이름 {{ isAddAccountMode ? '(새 계정)' : '' }}</FormLabel>
                            <FormInput v-model="formData.barobillMemberName" type="text" placeholder="계정정보 이름을 입력하세요"
                                :class="{ 'error': errors.barobillMemberName }" />
                            <ErrorMessage v-if="errors.barobillMemberName">{{ errors.barobillMemberName }}
                            </ErrorMessage>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>계정정보 - 아이디 {{ isAddAccountMode ? '(새 계정)' : '' }}</FormLabel>
                            <FormInput v-model="formData.barobillID" type="text" placeholder="바로빌 아이디를 입력하세요"
                                :class="{ 'error': errors.barobillID }" />
                            <ErrorMessage v-if="errors.barobillID">{{ errors.barobillID }}</ErrorMessage>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>계정정보 - 비밀번호 {{ isAddAccountMode ? '(새 계정)' : '' }}</FormLabel>
                            <FormInput v-model="formData.barobillPWD" type="password" placeholder="바로빌 비밀번호를 입력하세요"
                                :class="{ 'error': errors.barobillPWD }" />
                            <ErrorMessage v-if="errors.barobillPWD">{{ errors.barobillPWD }}</ErrorMessage>
                        </FormGroup>
                    </FormGrid3>

                    <FormGroup v-if="!isAddAccountMode" style="grid-column: span 2; margin-top: 15px;">
                        <FormLabel>주소</FormLabel>
                        <div style="display: flex; gap: 8px;">
                            <FormInput v-model="formData.barobillAddr1" type="text" placeholder="주소를 검색하세요" readonly
                                style="flex: 1;" />
                            <button type="button" @click="openDaumPostcode()"
                                style="padding: 12px 20px; background: #0f4c5c; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 600; white-space: nowrap;">
                                주소 찾기
                            </button>
                        </div>
                    </FormGroup>
                </FormSection>

                <!-- * 연결된 API 목록 -->
                <FormSection>
                    <SectionTitle>연결된 API 목록</SectionTitle>
                    <ApiListContainer>
                        <ApiListItem v-for="api in apiList" :key="api.id">
                            <ApiInfo>
                                <ApiHeader>
                                    <ApiName>{{ getApiDisplayName(api) }}</ApiName>
                                </ApiHeader>
                                <ApiDetails>
                                    <ApiDetailItem v-if="api.apiType === 'popbill'">
                                        <DetailLabel>아이디:</DetailLabel>
                                        <DetailValue :style="{ filter: isTestUser ? 'blur(4px)' : 'none' }">{{
                                            api.popbillCorpNum }}
                                        </DetailValue>
                                    </ApiDetailItem>
                                    <ApiDetailItem v-if="api.apiType === 'barobill'">
                                        <DetailLabel>아이디:</DetailLabel>
                                        <DetailValue :style="{ filter: isTestUser ? 'blur(4px)' : 'none' }">{{
                                            api.barobillID }}
                                        </DetailValue>
                                    </ApiDetailItem>
                                </ApiDetails>
                            </ApiInfo>
                            <ApiActions>
                                <RegisterAccountButton v-if="api.apiType === 'barobill'"
                                    @click="registerBankAccount(api)">
                                    <Wallet :size="16" :style="{ paddingTop: '2px' }" />
                                    계좌 등록
                                </RegisterAccountButton>
                                <RegisterAccountButton v-if="api.apiType === 'barobill'" @click="AddUserToCorp(api)">
                                    <AccountPlus :size="16" :style="{ paddingTop: '2px' }" />
                                    계정 추가
                                </RegisterAccountButton>
                                <EditButton v-if="api.apiType !== 'barobill'" @click="editApi(api)">
                                    <PencilIcon :size="16" />
                                    수정
                                </EditButton>
                                <DeleteButton v-if="api.apiType !== 'barobill'" @click="deleteApi(api.id)">
                                    <DeleteIcon :size="16" />
                                    해제
                                </DeleteButton>
                            </ApiActions>
                        </ApiListItem>

                        <EmptyState v-if="apiList.length === 0">
                            <EmptyIcon>🔑</EmptyIcon>
                            <EmptyText>연결된 API가 없습니다</EmptyText>
                            <EmptySubText>새로운 API를 연결해보세요</EmptySubText>
                        </EmptyState>
                    </ApiListContainer>
                </FormSection>
            </ModalContent>

            <ModalFooter>
                <FooterActions>
                    <CancelButton @click="closeModal">닫기</CancelButton>
                    <SaveButton @click="saveApi" :disabled="!isFormValid">
                        {{ isEditMode ? '수정' : '등록' }}
                    </SaveButton>
                </FooterActions>
            </ModalFooter>
        </ModalContainer>
    </ModalOverlay>
</template>

<script>
import * as ApiModalCss from '@/assets/styles/manage/modal/ApiModalCss.js';
import Wallet from 'vue-material-design-icons/Wallet.vue';
import PencilIcon from 'vue-material-design-icons/Pencil.vue';
import DeleteIcon from 'vue-material-design-icons/Delete.vue';
import TestIcon from 'vue-material-design-icons/Play.vue';
import AccountPlus from 'vue-material-design-icons/AccountPlus.vue';
import ListBoxOutline from 'vue-material-design-icons/ListBoxOutline.vue';

export default {
    name: 'ApiModal',

    components: {
        ...ApiModalCss,
        Wallet,
        PencilIcon,
        DeleteIcon,
        TestIcon,
        AccountPlus,
        ListBoxOutline
    },

    props: {
        show: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            formData: {
                apiType: '',
                popbillCorpNum: '',
                popbillApiKey: '',
                // 바로빌 필드
                barobillId: '',
                barobillApiKey: '',
                barobillCorpNum: '',
                barobillCorpName: '',
                barobillCEOName: '',
                barobillBizType: '',
                barobillBizClass: '',
                barobillAddr1: '',
                barobillMemberName: '',
                barobillID: '',
                barobillPWD: '',
                barobillTEL: '',
                barobillEmail: ''
            },
            selectedApiForBankAccount: null, // 계정 추가할 바로빌 API 정보
            isAddAccountMode: false,
            selectedBarobillForAddAccount: null,
            errors: {},
            isEditMode: false,
            editingApiId: null,
            apiList: []
        };
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },

        isFormValid() {
            if (!this.formData.apiType) return false;

            // ✅ 계정 추가 모드인 경우
            if (this.isAddAccountMode) {
                return this.formData.barobillMemberName.trim() &&
                    this.formData.barobillID.trim() &&
                    this.formData.barobillPWD.trim();
            }

            switch (this.formData.apiType) {
                case 'popbill':
                    return this.formData.popbillCorpNum.trim() && this.formData.popbillApiKey.trim();
                case 'barobill':
                    return this.formData.barobillCorpNum.trim() &&
                        this.formData.barobillCorpName.trim() &&
                        this.formData.barobillCEOName.trim() &&
                        this.formData.barobillMemberName.trim() &&
                        this.formData.barobillID.trim() &&
                        this.formData.barobillPWD.trim();
                default:
                    return false;
            }
        }
    },

    watch: {
        show(newVal) {
            if (newVal) {
                document.addEventListener('keydown', this.handleEscKey);
            } else {
                document.removeEventListener('keydown', this.handleEscKey);
            }
        }
    },

    methods: {
        handleEscKey(event) {
            if (event.key === 'Escape' || event.keyCode === 27) {
                this.closeModal();
            }
        },

        // 계정 추가
        AddUserToCorp(api) {
            // 계정 추가 모드로 전환
            this.isAddAccountMode = true;
            this.isEditMode = false;
            this.selectedBarobillForAddAccount = api;

            // 기존 회사 정보는 유지하고 계정 정보만 초기화
            this.formData = {
                apiType: 'barobill',
                popbillCorpNum: '',
                popbillApiKey: '',
                // 바로빌 - 회사 정보는 기존 데이터 유지 (readonly로 표시)
                barobillId: api.barobillId || '',
                barobillCorpNum: api.barobillCorpNum || '',
                barobillCorpName: api.barobillCorpName || '',
                barobillCEOName: api.barobillCEOName || '',
                barobillBizType: api.barobillBizType || '',
                barobillBizClass: api.barobillBizClass || '',
                barobillAddr1: api.barobillAddr1 || '',
                // 계정 정보만 초기화 (사용자가 새로 입력)
                barobillMemberName: '',
                barobillID: '',
                barobillPWD: '',
                barobillTEL: '',
                barobillEmail: ''
            };

            this.$swal.fire({
                icon: 'info',
                title: '계정 추가',
                text: '추가할 계정 정보를 입력해주세요.',
                timer: 2000,
                showConfirmButton: false
            });
        },

        // 다음 우편번호 서비스 스크립트 동적 로딩
        loadDaumPostcodeScript() {
            return new Promise((resolve, reject) => {
                // 이미 로드되어 있으면 바로 resolve
                if (window.daum && window.daum.Postcode) {
                    resolve();
                    return;
                }

                // 스크립트가 이미 추가되어 있는지 확인
                const existingScript = document.querySelector('script[src*="postcode.v2.js"]');
                if (existingScript) {
                    existingScript.onload = resolve;
                    existingScript.onerror = reject;
                    return;
                }

                // 새 스크립트 태그 생성
                const script = document.createElement('script');
                script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
                script.onload = resolve;
                script.onerror = reject;

                document.head.appendChild(script);
            });
        },

        // 개선된 주소 검색 함수
        async openDaumPostcode(type) {
            try {
                // 다음 우편번호 서비스 로드 확인
                await this.loadDaumPostcodeScript();

                const width = 500;
                const height = 600;
                const left = Math.ceil((window.innerWidth - width) / 2);
                const top = Math.ceil((window.innerHeight - height) / 2);

                new window.daum.Postcode({
                    oncomplete: (data) => {
                        this.formData.barobillAddr1 = data.address;
                    },
                    width: width,
                    height: height,
                    left: left,
                    top: top
                }).open();

            } catch (error) {
                console.error('주소 검색 서비스 로딩 실패:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '주소 검색 서비스를 불러올 수 없습니다.\n인터넷 연결을 확인하고 다시 시도해주세요.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        closeModal() {
            this.$emit('close');
            this.resetForm();
        },

        resetForm() {
            this.formData = {
                apiType: '',
                popbillCorpNum: '',
                popbillApiKey: '',
                thebillId: '',
                thebillApiKey: '',
                barobillId: '',
                barobillApiKey: '',
                barobillCorpNum: '',
                barobillCorpName: '',
                barobillCEOName: '',
                barobillBizType: '',
                barobillBizClass: '',
                barobillAddr1: '',
                barobillMemberName: '',
                barobillID: '',
                barobillPWD: '',
                barobillTEL: '',
                barobillEmail: ''
            };
            this.selectedApiForBankAccount = null;
            this.errors = {};
            this.isEditMode = false;
            this.editingApiId = null;
            this.isAddAccountMode = false;
            this.selectedBarobillForAddAccount = null;
        },

        onApiTypeChange() {
            this.errors = {};

            if (this.apiList && this.apiList.length > 0) {
                const selectedApi = this.apiList.find(api => api.apiType === this.formData.apiType);

                if (selectedApi) {
                    // 해당 API의 데이터를 폼에 자동으로 채움
                    if (selectedApi.apiType === 'popbill') {
                        this.formData.popbillCorpNum = selectedApi.popbillCorpNum || '';
                        this.formData.popbillApiKey = selectedApi.popbillApiKey || '';
                    } else if (selectedApi.apiType === 'thebill') {
                        this.formData.thebillId = selectedApi.thebillId || '';
                        this.formData.thebillApiKey = selectedApi.thebillApiKey || '';
                    } else if (selectedApi.apiType === 'barobill') {
                        this.formData.barobillId = selectedApi.barobillId || '';
                        this.formData.barobillCorpNum = selectedApi.barobillCorpNum || '';
                        this.formData.barobillCorpName = selectedApi.barobillCorpName || '';
                        this.formData.barobillCEOName = selectedApi.barobillCEOName || '';
                        this.formData.barobillBizType = selectedApi.barobillBizType || '';
                        this.formData.barobillBizClass = selectedApi.barobillBizClass || '';
                        this.formData.barobillAddr1 = selectedApi.barobillAddr1 || '';
                        this.formData.barobillMemberName = selectedApi.barobillMemberName || '';
                        this.formData.barobillID = selectedApi.barobillID || '';
                        this.formData.barobillPWD = selectedApi.barobillPWD || '';
                        this.formData.barobillTEL = selectedApi.barobillTEL || '';
                        this.formData.barobillEmail = selectedApi.barobillEmail || '';
                    }
                }
            }
        },

        validateForm() {
            this.errors = {};

            if (!this.formData.apiType) {
                this.errors.apiType = 'API 서비스를 선택해주세요';
                return false;
            }

            switch (this.formData.apiType) {
                case 'popbill':
                    if (!this.formData.popbillCorpNum.trim()) {
                        this.errors.popbillCorpNum = '사업자번호를 입력해주세요';
                    }
                    if (!this.formData.popbillApiKey.trim()) {
                        this.errors.popbillApiKey = '팝빌 API Key를 입력해주세요';
                    }
                    break;
                case 'barobill':
                    if (!this.formData.barobillCorpNum.trim()) {
                        this.errors.barobillCorpNum = '사업자번호를 입력해주세요';
                    }
                    if (!this.formData.barobillCorpName.trim()) {
                        this.errors.barobillCorpName = '회사명을 입력해주세요';
                    }
                    if (!this.formData.barobillCEOName.trim()) {
                        this.errors.barobillCEOName = '대표자명을 입력해주세요';
                    }
                    if (!this.formData.barobillMemberName.trim()) {
                        this.errors.barobillMemberName = '계정정보 이름을 입력해주세요';
                    }
                    if (!this.formData.barobillID.trim()) {
                        this.errors.barobillID = '아이디를 입력해주세요';
                    }
                    if (!this.formData.barobillPWD.trim()) {
                        this.errors.barobillPWD = '비밀번호를 입력해주세요';
                    }
                    break;
            }

            return Object.keys(this.errors).length === 0;
        },

        async registerBankAccount(api) {
            const { value: formValues } = await this.$swal.fire({
                title: '<div style="font-size: 20px; font-weight: 700; color: #0f4c5c; margin-bottom: 5px;">계좌 등록</div>',
                html: `
            <style>
                .bank-account-form {
                    text-align: left;
                    max-width: 480px;
                    margin: 0 auto;
                    max-height: 500px;
                    overflow-y: auto;
                    padding-right: 8px;
                }
                .bank-account-form::-webkit-scrollbar {
                    width: 6px;
                }
                .bank-account-form::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                .bank-account-form::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 10px;
                }
                .bank-account-form::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }
                .form-field {
                    margin-bottom: 12px;
                }
                .form-field label {
                    display: block;
                    margin-bottom: 4px;
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                }
                .form-field input,
                .form-field select {
                    width: 100%;
                    padding: 8px 12px;
                    font-size: 16px;
                    border: 1.5px solid #e0e0e0;
                    border-radius: 6px;
                    margin: 0;
                    transition: all 0.2s ease;
                    box-sizing: border-box;
                }
                .form-field input:focus,
                .form-field select:focus {
                    outline: none;
                    border-color: #0f4c5c;
                    box-shadow: 0 0 0 3px rgba(15, 76, 92, 0.1);
                }
                .form-field small {
                    display: block;
                    margin-top: 3px;
                    font-size: 16px;
                }
                .form-field small.info {
                    color: #2196F3;
                }
                .form-field small.example {
                    color: #666;
                }
            </style>
            <div class="bank-account-form">
                <div class="form-field">
                    <label>은행</label>
                    <select id="Bank" class="swal2-input">
                        <option value="">선택하세요</option>
                        <option value="KB">국민은행</option>
                        <option value="SHINHAN">신한은행</option>
                        <option value="NH">농협은행</option>
                        <option value="HANA">하나은행</option>
                        <option value="SC">제일은행</option>
                        <option value="WOORI">우리은행</option>
                        <option value="IBK">기업은행</option>
                        <option value="KDB">산업은행</option>
                        <option value="KFCC">새마을금고</option>
                        <option value="CITI">씨티은행</option>
                        <option value="SUHYUP">수협은행</option>
                        <option value="EPOST">우체국</option>
                        <option value="KJBANK">광주은행</option>
                        <option value="JBBANK">전북은행</option>
                        <option value="DGB">대구은행</option>
                        <option value="BUSANBANK">부산은행</option>
                        <option value="KNBANK">경남은행</option>
                        <option value="EJEJUBANK">제주은행</option>
                        <option value="KBANK">케이뱅크</option>
                    </select>
                </div>
                
                <div class="form-field">
                    <label>계좌 유형</label>
                    <select id="BankAccountType" class="swal2-input">
                        <option value="">선택하세요</option>
                        <option value="C">법인계좌</option>
                        <option value="P">개인계좌</option>
                    </select>
                </div>
                
                <div class="form-field">
                    <label>계좌번호</label>
                    <input id="BankAccountNum" type="text" class="swal2-input" placeholder="계좌번호 (- 제외)">
                </div>
                
                <div class="form-field">
                    <label>계좌 비밀번호</label>
                    <input id="BankAccountPwd" type="password" class="swal2-input" placeholder="계좌 비밀번호">
                </div>
                
                <div class="form-field">
                    <label>빠른조회 아이디</label>
                    <input id="WebId" type="text" class="swal2-input" placeholder="빠른조회 아이디">
                    <small class="info">- 국민은행, 신한은행, 신협은행, 대구은행은 필수입니다.</small>
                </div>
                
                <div class="form-field">
                    <label>빠른조회 비밀번호</label>
                    <input id="WebPwd" type="password" class="swal2-input" placeholder="빠른조회 비밀번호">
                    <small class="info">- 신한은행, 신협은행, 대구은행은 필수입니다.</small>
                </div>
                
                <div class="form-field">
                    <label>사업자번호</label>
                    <input id="IdentityNum" type="text" class="swal2-input" placeholder="사업자번호 (- 제외)">
                </div>
            </div>
        `,
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: '등록',
                cancelButtonText: '취소',
                confirmButtonColor: '#0f4c5c',
                cancelButtonColor: '#6c757d',
                width: '600px',
                customClass: {
                    container: 'swal-bank-account-modal',
                    popup: 'swal-bank-account-popup'
                },
                preConfirm: () => {
                    const bank = document.getElementById('Bank').value;
                    const bankAccountType = document.getElementById('BankAccountType').value;
                    const bankAccountNum = document.getElementById('BankAccountNum').value;
                    const bankAccountPwd = document.getElementById('BankAccountPwd').value;
                    const webId = document.getElementById('WebId').value;
                    const webPwd = document.getElementById('WebPwd').value;
                    const identityNum = document.getElementById('IdentityNum').value;

                    // 필수 항목 검증
                    if (!bank || !bankAccountType || !bankAccountNum || !bankAccountPwd ||
                        !webId || !webPwd || !identityNum) {
                        this.$swal.showValidationMessage('필수 항목을 모두 입력해주세요');
                        return false;
                    }

                    return {
                        collectCycle: 'DAY1', // 항상 DAY1으로 고정
                        bank,
                        bankAccountType,
                        bankAccountNum,
                        bankAccountPwd,
                        webId,
                        webPwd,
                        identityNum
                    };
                }
            });

            if (formValues) {
                // 계좌 등록 API 호출
                this.$swal.fire({
                    title: '계좌 등록 중...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                try {
                    const response = await this.$axios.post(
                        `${this.$apiUrls.hanqmedi}/barobill/RegistBankAccountEx`,
                        {
                            ...formValues,
                            corpNum: api.barobillCorpNum,
                            companykey: this.$globalState.company_key
                        }
                    );

                    if (response.data.success) {
                        this.$swal.fire({
                            icon: 'success',
                            title: '계좌 등록 완료',
                            text: '계좌가 성공적으로 등록되었습니다.',
                            confirmButtonText: '확인',
                            confirmButtonColor: '#0f4c5c'
                        });
                    } else {
                        throw new Error(response.data.message || '계좌 등록에 실패했습니다.');
                    }
                } catch (error) {
                    console.error('계좌 등록 실패:', error);

                    let errorMessage = '계좌 등록 중 오류가 발생했습니다.';
                    if (error.response?.data?.message) {
                        errorMessage = error.response.data.message;
                    } else if (error.message) {
                        errorMessage = error.message;
                    }

                    this.$swal.fire({
                        icon: 'error',
                        title: '계좌 등록 실패',
                        text: errorMessage,
                        confirmButtonText: '확인',
                        confirmButtonColor: '#e74c3c'
                    });
                }
            }
        },

        async saveApi() {
            if (!this.validateForm()) {
                return;
            }

            try {
                // 계정 추가 모드인 경우
                if (this.isAddAccountMode) {
                    await this.addBarobillAccount();
                    return;
                }

                const apiData = {
                    apiType: this.formData.apiType,
                    companykey: this.$globalState.company_key,
                };

                switch (this.formData.apiType) {
                    case 'popbill':
                        apiData.popbillCorpNum = this.formData.popbillCorpNum;
                        apiData.popbillApiKey = this.formData.popbillApiKey;
                        break;
                    case 'thebill':
                        apiData.thebillId = this.formData.thebillId;
                        apiData.thebillApiKey = this.formData.thebillApiKey;
                        break;
                    case 'barobill':
                        await this.registerBarobill();

                        apiData.barobillCorpNum = this.formData.barobillCorpNum;
                        apiData.barobillCorpName = this.formData.barobillCorpName;
                        apiData.barobillCEOName = this.formData.barobillCEOName;
                        apiData.barobillBizType = this.formData.barobillBizType;
                        apiData.barobillBizClass = this.formData.barobillBizClass;
                        apiData.barobillAddr1 = this.formData.barobillAddr1;
                        apiData.barobillMemberName = this.formData.barobillMemberName;
                        apiData.barobillID = this.formData.barobillID;
                        apiData.barobillPWD = this.formData.barobillPWD;
                        apiData.barobillTEL = this.formData.barobillTEL;
                        apiData.barobillEmail = this.formData.barobillEmail;
                        break;
                }

                this.$swal.fire({
                    title: '등록 중...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                if (this.isEditMode) {
                    const response = await this.$axios.put(
                        `${this.$apiUrls.hanqmedi}/updateApi/${this.editingApiId}`,
                        apiData
                    );

                    if (response.data.success) {
                        this.$swal.fire({
                            icon: 'success',
                            title: '성공',
                            text: 'API가 수정되었습니다.',
                            confirmButtonText: '확인'
                        });
                        this.loadApiList();
                        this.resetForm();
                        this.$emit('api-updated');
                    }
                } else {
                    const response = await this.$axios.post(
                        `${this.$apiUrls.hanqmedi}/registerApi`,
                        apiData
                    );

                    if (response.data.success) {
                        this.$swal.fire({
                            icon: 'success',
                            title: '성공',
                            text: 'API가 연결되었습니다.',
                            confirmButtonText: '확인'
                        });
                        this.loadApiList();
                        this.resetForm();
                        this.$emit('api-updated');
                    }
                }
            } catch (error) {
                console.error('API 저장 오류:', error);
                let errorMessage = 'API 저장 중 오류가 발생했습니다.';

                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '오류',
                    text: errorMessage,
                    confirmButtonText: '확인'
                });
            }
        },

        // ✅ 바로빌 계정 추가 메서드
        async addBarobillAccount() {
            // 필수 필드 검증
            if (!this.formData.barobillMemberName || !this.formData.barobillID ||
                !this.formData.barobillPWD) {
                this.$swal.fire({
                    icon: 'error',
                    title: '입력 오류',
                    text: '계정 정보를 모두 입력해주세요'
                });
                return;
            }

            this.$swal.fire({
                title: '계정 추가 중...',
                allowOutsideClick: false,
                didOpen: () => {
                    this.$swal.showLoading();
                }
            });

            try {
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/barobill/AddUserToCorp`,
                    {
                        corpNum: this.formData.barobillCorpNum,
                        memberName: this.formData.barobillMemberName,
                        id: this.formData.barobillID,
                        pwd: this.formData.barobillPWD,
                        tel: this.formData.barobillTEL,
                        hp: '', // 필요시 추가
                        email: this.formData.barobillEmail,
                        grade: 2, // 기본 등급 (필요시 수정)
                        companykey: this.$globalState.company_key
                    }
                );

                if (response.data.success) {
                    this.$swal.fire({
                        icon: 'success',
                        title: '계정 추가 완료',
                        text: '새로운 계정이 성공적으로 추가되었습니다.',
                        confirmButtonText: '확인'
                    });
                    this.resetForm();
                    this.$emit('api-updated');
                } else {
                    throw new Error(response.data.message || '계정 추가에 실패했습니다.');
                }
            } catch (error) {
                console.error('계정 추가 실패:', error);
                let errorMessage = '계정 추가 중 오류가 발생했습니다.';

                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '계정 추가 실패',
                    text: errorMessage,
                    confirmButtonText: '확인'
                });
            }
        },

        async registerBarobill() {
            if (!this.formData.barobillCorpNum || !this.formData.barobillCorpName ||
                !this.formData.barobillCEOName || !this.formData.barobillMemberName ||
                !this.formData.barobillID || !this.formData.barobillPWD) {
                throw new Error('필수 항목을 모두 입력해주세요');
            }

            try {
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/barobill/RegistCorp`,
                    {
                        corpNum: this.formData.barobillCorpNum,
                        corpName: this.formData.barobillCorpName,
                        ceoName: this.formData.barobillCEOName,
                        bizType: this.formData.barobillBizType,
                        bizClass: this.formData.barobillBizClass,
                        addr1: this.formData.barobillAddr1,
                        memberName: this.formData.barobillMemberName,
                        id: this.formData.barobillID,
                        pwd: this.formData.barobillPWD,
                        tel: this.formData.barobillTEL,
                        email: this.formData.barobillEmail,
                        companykey: this.$globalState.company_key
                    }
                );

                if (!response.data.success) {
                    throw new Error(response.data.message || '바로빌 회원사 가입에 실패했습니다.');
                }

                console.log('바로빌 회원사 가입 성공');
                return response;
            } catch (error) {
                console.error('바로빌 회원사 가입 실패:', error);
                throw error;
            }
        },

        async loadApiList() {
            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getApiList`,
                    {
                        params: {
                            companykey: this.$globalState.company_key
                        }
                    }
                );

                console.log('API 목록 응답:', response.data);

                if (response.data.success) {
                    this.apiList = response.data.data;
                    this.loadRegisteredApiToForm();
                }
            } catch (error) {
                console.error('API 목록 로드 오류:', error);
                this.apiList = [];
            }
        },

        // 등록된 API 데이터를 폼에 자동 로드
        loadRegisteredApiToForm() {
            if (!this.apiList || this.apiList.length === 0) return;

            this.apiList.forEach(api => {
                if (api.apiType === 'popbill') {
                    // 팝빌 데이터 로드
                    this.formData.popbillCorpNum = api.popbillCorpNum || '';
                    this.formData.popbillApiKey = api.popbillApiKey || '';
                } else if (api.apiType === 'barobill') {
                    // 바로빌 데이터 로드
                    this.formData.barobillId = api.barobillId || '';
                    this.formData.barobillCorpNum = api.barobillCorpNum || '';
                    this.formData.barobillCorpName = api.barobillCorpName || '';
                    this.formData.barobillCEOName = api.barobillCEOName || '';
                    this.formData.barobillBizType = api.barobillBizType || '';
                    this.formData.barobillBizClass = api.barobillBizClass || '';
                    this.formData.barobillAddr1 = api.barobillAddr1 || '';
                    this.formData.barobillMemberName = api.barobillMemberName || '';
                    this.formData.barobillID = api.barobillID || '';
                    this.formData.barobillPWD = api.barobillPWD || '';
                    this.formData.barobillTEL = api.barobillTEL || '';
                    this.formData.barobillEmail = api.barobillEmail || '';
                }
            });

            // 첫 번째 등록된 API를 기본 선택으로 설정 (선택 사항)
            if (this.apiList.length > 0 && !this.formData.apiType) {
                this.formData.apiType = this.apiList[0].apiType;
            }
        },

        editApi(api) {
            this.isEditMode = true;
            this.editingApiId = api.id;

            // API 타입별로 폼 데이터 설정 - 기존 값들을 정확히 매핑
            if (api.apiType === 'popbill') {
                this.formData = {
                    apiType: 'popbill',
                    popbillCorpNum: api.popbillCorpNum || '',
                    popbillApiKey: api.popbillApiKey || '',
                    thebillId: '',
                    thebillApiKey: '',
                    barobillId: '',
                    barobillApiKey: '',
                };
            } else if (api.apiType === 'thebill') {
                this.formData = {
                    apiType: 'thebill',
                    popbillCorpNum: '',
                    popbillApiKey: '',
                    thebillId: api.thebillId || '',
                    thebillApiKey: api.thebillApiKey || '',
                    barobillId: '',
                    barobillApiKey: '',
                };
            } else if (api.apiType === 'barobill') {
                this.formData = {
                    apiType: 'barobill',
                    popbillCorpNum: '',
                    popbillApiKey: '',
                    thebillId: '',
                    thebillApiKey: '',
                    // 바로빌 모든 필드
                    barobillId: api.barobillId || '',
                    barobillCorpNum: api.barobillCorpNum || '',
                    barobillCorpName: api.barobillCorpName || '',
                    barobillCEOName: api.barobillCEOName || '',
                    barobillBizType: api.barobillBizType || '',
                    barobillBizClass: api.barobillBizClass || '',
                    barobillAddr1: api.barobillAddr1 || '',
                    barobillMemberName: api.barobillMemberName || '',
                    barobillID: api.barobillID || '',
                    barobillPWD: api.barobillPWD || '',
                    barobillTEL: api.barobillTEL || '',
                    barobillEmail: api.barobillEmail || ''
                };
            }
        },

        async deleteApi(apiId) {
            try {
                const result = await this.$swal.fire({
                    title: 'API 연결 해제',
                    text: '정말로 이 API 연결을 해제하시겠습니까?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '해제',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6'
                });

                if (result.isConfirmed) {
                    const response = await this.$axios.delete(
                        `${this.$apiUrls.hanqmedi}/deleteApi/${apiId}`
                    );

                    if (response.data.success) {
                        this.$swal.fire('성공', 'API 연결이 해제되었습니다.', 'success');

                        if (this.editingApiId === apiId) {
                            this.resetForm();
                        }

                        this.loadApiList();
                        this.$emit('api-updated'); // 부모 컴포넌트에 알림
                    } else {
                        this.$swal.fire('오류', response.data.message || 'API 연결 해제에 실패했습니다.', 'error');
                    }
                }
            } catch (error) {
                console.error('API 삭제 오류:', error);
                let errorMessage = 'API 삭제 중 오류가 발생했습니다.';

                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                this.$swal.fire('오류', errorMessage, 'error');
            }
        },

        getApiDisplayName(api) {
            switch (api.apiType) {
                case 'popbill':
                    return '팝빌';
                case 'barobill':
                    return '바로빌';
                default:
                    return '알 수 없는 API';
            }
        },
    },

    mounted() {
        this.loadApiList();
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.handleEscKey);
    }
};
</script>

<style>
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>